import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, Brain, CheckCircle2, Clock, Code2, FileText,
  Layers, MessageSquare, Mic, ScanSearch, ShieldCheck, Sparkles,
} from 'lucide-react';
import { CTAPair, FaqAccordion, type FaqItem } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /assessments — "Anatomy of a Kollez question" format.
 *
 * Every other marketing page has its own shape. /assessments is
 * diagrammatic: one real question dissected, with annotation callouts
 * pointing to every metadata field the platform tracks (stem, options,
 * difficulty, skill, source, quality signals, attempts, integrity
 * flags, badge outcome).
 *
 * Then: five ways the same question is delivered (fixed / adaptive /
 * chat / code / voice), how the bank gets built, how badges follow
 * students, how integrity is protected. All grounded in the shipping
 * data model (Assessment, Question, QuestionBank, Badge, IntegrityFlag).
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (assessments gets the anatomy-of-a-question format).
 */

/* ────────── the anchor question ────────── */

const QUESTION_STEM = 'To rank employees by salary within each department without collapsing rows, which SQL feature would you use?';
const QUESTION_OPTIONS = [
  { key: 'a', text: 'A GROUP BY with MAX()', correct: false },
  { key: 'b', text: 'A subquery per row',    correct: false },
  { key: 'c', text: 'A window function (ROW_NUMBER / RANK)', correct: true },
  { key: 'd', text: 'A common table expression (CTE)', correct: false },
];

const ANATOMY_FIELDS: Array<{ label: string; value: React.ReactNode; hint: string }> = [
  { label: 'Skill',       value: 'sql',                                     hint: 'Canonical slug from SkillTaxonomy. Badges match on this.' },
  { label: 'Difficulty',  value: <span style={{ color: 'rgb(var(--ink-warning))', fontWeight: 600 }}>medium</span>, hint: 'easy / medium / hard. Draw specs mix by ratio.' },
  { label: 'Marks',       value: '3',                                       hint: 'Total marks awarded on correct.' },
  { label: 'Time guide',  value: '90 s',                                    hint: 'Advisory only. Not a countdown per-question.' },
  { label: 'Wrong penalty', value: '−1',                                    hint: 'Negative marking, opt-in per assessment.' },
  { label: 'Type',        value: 'single',                                  hint: 'One of 12 question types. This one is single-choice.' },
  { label: 'Source',      value: <span style={{ color: 'rgb(var(--ink-success))' }}>manual</span>, hint: 'Author was a human. `ai` sources go through review.' },
  { label: 'Status',      value: <span style={{ color: 'rgb(var(--ink-success))' }}>approved</span>, hint: 'draft / review / approved / retired.' },
  { label: 'Bank',        value: 'Data & Analytics · Q3 2026',              hint: 'Tenant-scoped namespace. Same bank can back many assessments.' },
];

const QUALITY_SIGNALS: Array<{ label: string; value: string; hint: string; tone: 'good' | 'ok' | 'watch' }> = [
  { label: 'pCorrect',   value: '58%',        hint: '% of attempts that answered correctly. Sweet spot: 40-70%.', tone: 'good' },
  { label: 'Discrim',    value: 'good',        hint: 'How well the question separates strong from weak candidates.', tone: 'good' },
  { label: 'Times used', value: '1,242',       hint: 'Ever attempted across all drives that included it.',    tone: 'ok'   },
  { label: 'Flags',      value: 'none',        hint: 'Auto-flags: too-easy · key · inverted · duplicate.',    tone: 'good' },
];

/* ────────── mode-mock: same question, different delivery ────────── */

interface Mode {
  key: string;
  icon: React.ReactNode;
  name: string;
  hint: string;
  render: React.ReactNode;
  who: string;
}

function FixedMock() {
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium">Q 18 of 36 · same for everyone</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgb(var(--bg-warning))', color: 'rgb(var(--ink-warning))', fontWeight: 600 }}>MEDIUM</span>
      </div>
      <div className="p-3 grid gap-2 text-xs">
        <p className="m-0" style={{ color: 'rgb(var(--ink-primary))' }}>{QUESTION_STEM}</p>
        <div className="grid gap-1 mt-1">
          {QUESTION_OPTIONS.map((o) => (
            <div key={o.key} className="rounded border px-2 py-1.5 flex items-center gap-2" style={{ borderColor: 'rgb(var(--line-secondary))' }}>
              <span className="text-[10px] font-semibold rounded flex items-center justify-center" style={{ width: 16, height: 16, background: 'rgb(var(--bg-tertiary))' }}>{o.key.toUpperCase()}</span>
              <span>{o.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdaptiveMock() {
  const [step, setStep] = useState(1);
  const levels = ['easy', 'medium', 'hard'];
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium">Adaptive · draws from bank</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
          background: step === 1 ? 'rgb(var(--bg-success))' : step === 2 ? 'rgb(var(--bg-warning))' : 'rgb(var(--bg-danger))',
          color: step === 1 ? 'rgb(var(--ink-success))' : step === 2 ? 'rgb(var(--ink-warning))' : 'rgb(var(--ink-danger))',
          fontWeight: 600,
          textTransform: 'uppercase',
        }}>{levels[step]}</span>
      </div>
      <div className="p-3 grid gap-2 text-xs">
        <p className="m-0" style={{ color: 'rgb(var(--ink-primary))' }}>
          {step === 0 && 'What clause sorts SQL result rows?'}
          {step === 1 && QUESTION_STEM}
          {step === 2 && 'Given a partition and an ordering, how does RANK() differ from DENSE_RANK() on ties?'}
        </p>
        <div className="flex gap-2 mt-2">
          {[0,1,2].map((i) => (
            <button
              key={i}
              type="button"
              className="text-[10px] px-2 py-1 rounded"
              style={{
                background: step === i ? 'rgb(var(--ink-accent))' : 'rgb(var(--bg-tertiary))',
                color: step === i ? 'rgb(var(--ink-on-accent))' : 'rgb(var(--ink-secondary))',
                cursor: 'pointer',
              }}
              onClick={() => setStep(i)}
            >
              answer {i === 0 ? 'wrong' : i === 1 ? 'right' : 'right again'}
            </button>
          ))}
        </div>
        <p className="text-[10px] m-0 mt-1" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Difficulty adapts. Fewer questions, sharper signal per attempt.
        </p>
      </div>
    </div>
  );
}

function ChatMock() {
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium flex items-center gap-1.5"><Sparkles size={11} style={{ color: 'rgb(var(--ink-accent))' }} />Nexie · SQL mock interview</span>
      </div>
      <div className="p-3 grid gap-2 text-xs">
        <div className="rounded-2xl rounded-bl-sm px-2.5 py-2 max-w-[92%]" style={{ background: 'rgb(var(--bg-info))' }}>
          Given a table of employees with salary and department, how would you rank the top three earners per department?
        </div>
        <div className="rounded-2xl rounded-br-sm px-2.5 py-2 self-end max-w-[75%]" style={{ background: 'rgb(var(--bg-tertiary))' }}>
          A window function with PARTITION BY department
        </div>
        <div className="rounded-2xl rounded-bl-sm px-2.5 py-2 max-w-[92%]" style={{ background: 'rgb(var(--bg-info))' }}>
          Good — RANK, DENSE_RANK, or ROW_NUMBER? What if two people tie?
        </div>
        <p className="text-[10px] m-0 mt-1" style={{ color: 'rgb(var(--ink-tertiary))' }}>Follow-ups grounded in your last answer.</p>
      </div>
    </div>
  );
}

function CodeMock() {
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium">Live coding · sandbox</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))', fontWeight: 600 }}>SQL</span>
      </div>
      <div className="p-3 grid gap-2 text-xs">
        <p className="m-0" style={{ color: 'rgb(var(--ink-primary))' }}>Return the top-2 earners per department.</p>
        <pre
          className="rounded p-2 overflow-x-auto text-[10.5px] m-0"
          style={{
            background: 'rgb(var(--ink-primary))',
            color: 'rgb(var(--bg-primary))',
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            lineHeight: 1.5,
          }}
        >
{`SELECT * FROM (
  SELECT emp, dept, salary,
    ROW_NUMBER() OVER (
      PARTITION BY dept ORDER BY salary DESC
    ) AS rn
  FROM employees
) WHERE rn <= 2;`}
        </pre>
        <div className="flex items-center gap-2 text-[10px]" style={{ color: 'rgb(var(--ink-success))' }}>
          <CheckCircle2 size={10} /> 5 of 5 test cases pass
        </div>
      </div>
    </div>
  );
}

function CaseMock() {
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium">Take-home · case · 6 hours</span>
      </div>
      <div className="p-3 grid gap-2 text-xs">
        <p className="m-0 font-medium" style={{ color: 'rgb(var(--ink-primary))' }}>
          Rank-per-partition in SQL — the analyst view
        </p>
        <p className="m-0" style={{ color: 'rgb(var(--ink-secondary))' }}>
          An analytics team ships a monthly leaderboard of the top 3 sellers per region. Explain three shapes the query could take (window function, correlated subquery, self-join), and which you&apos;d choose for a 50M-row table. Submit as a Google Doc or repo.
        </p>
        <div className="rounded p-2 text-[10px]" style={{ background: 'rgb(var(--bg-secondary))' }}>
          <span className="font-medium">Rubric:</span> correctness (40) · trade-off reasoning (40) · code quality (20)
        </div>
      </div>
    </div>
  );
}

function VoiceMock() {
  return (
    <div className="rounded-lg overflow-hidden card">
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
        <span className="text-xs font-medium">Voice · behavioural</span>
        <span className="text-[10px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>02:14</span>
      </div>
      <div className="p-3 grid gap-3 text-xs">
        <p className="m-0" style={{ color: 'rgb(var(--ink-primary))' }}>
          Tell us about a time you had to explain a complex SQL query to a non-technical stakeholder.
        </p>
        <div className="flex items-end gap-0.5 h-8">
          {Array.from({ length: 48 }).map((_, i) => (
            <span
              key={i}
              style={{
                width: 3,
                height: `${20 + Math.abs(Math.sin(i * 0.7)) * 60}%`,
                background: i < 30 ? 'rgb(var(--ink-accent))' : 'rgb(var(--bg-tertiary))',
                borderRadius: 2,
                display: 'block',
              }}
            />
          ))}
        </div>
        <p className="text-[10px] m-0" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Recording transcribed, rubric-scored by Nexie; human reviews the top n.
        </p>
      </div>
    </div>
  );
}

const MODES: Mode[] = [
  { key: 'fixed',     icon: <Layers size={16} />,         name: 'Fixed question set',       hint: 'Same 30 for everyone. Shuffled per attempt.',       who: 'Screening drives',      render: <FixedMock /> },
  { key: 'adaptive',  icon: <Brain size={16} />,          name: 'Adaptive from a bank',     hint: 'Difficulty adapts to your last answer.',            who: 'Practice + assessments', render: <AdaptiveMock /> },
  { key: 'chat',      icon: <MessageSquare size={16} />,  name: 'Chat (Nexie)',             hint: 'Follow-ups grounded in your last answer.',          who: 'Mock interviews',        render: <ChatMock /> },
  { key: 'code',      icon: <Code2 size={16} />,          name: 'Live coding',              hint: 'Real editor, real test cases.',                     who: 'Engineering roles',      render: <CodeMock /> },
  { key: 'case',      icon: <FileText size={16} />,       name: 'Take-home / case',         hint: 'Open-ended, rubric-graded.',                        who: 'Product, business',      render: <CaseMock /> },
  { key: 'voice',     icon: <Mic size={16} />,            name: 'Voice / video',            hint: 'Recorded, transcribed, rubric-scored.',             who: 'HR, behavioural',        render: <VoiceMock /> },
];

/* ────────── FAQ, badges, ethics ────────── */

const FAQ: FaqItem[] = [
  { q: 'Can our institution write our own questions?',
    a: 'Yes. Every institution has its own private question bank. Twelve question types (single/multi/tf/blank/num/match/order/short/long/code/case/voice). Your HoD or TPO authors; Nexie can suggest a first draft; a reviewer must approve before it goes live.' },
  { q: 'Does Nexie generate questions?',
    a: 'Yes — from a role description or a topic. Every AI-drafted question carries a `source: ai` marker and is queued for human review. You keep the same audit trail whether the author is a person or Nexie.' },
  { q: 'What stops a student from copy-pasting an answer?',
    a: 'Seven kinds of integrity flag: paste, tab-switch, answer-match with another candidate, impossible-speed, code-similarity, network-timing, face-mismatch. No auto-void; every flag is reviewed. 7-day appeal on any void.' },
  { q: 'How does the "portable badge" work?',
    a: 'A graded pass emits a Badge owned by the student. Any future opportunity whose workflow includes the same skill + level auto-satisfies the stage. Student skips the retake. Consent-controlled per DPDP.' },
  { q: 'What if a student needs extra time?',
    a: 'Accommodations are per-candidate — set an extra-time percentage once, applied to every assessment. The proctoring does not treat accommodated time as suspicious.' },
  { q: 'Can partners set their own assessments?',
    a: 'Yes. Fixed screening test, draw from their own bank, or ask Nexie to draft a fresh 20-question set from the JD. Partner reviews and approves before it goes live.' },
  { q: 'What data do you keep about attempts?',
    a: 'Score, answer history, integrity flags, and proctoring artifacts (only if enabled). Retention varies by assessment type. Students can export or delete their own attempt data.' },
];

/* ────────── page ────────── */

export function AssessmentsPage() {
  return (
    <>
      <PageMeta
        title="Assessments"
        description="Anatomy of a Kollez question — one real SQL question, dissected. Every field the platform tracks, every mode it can be delivered in, every integrity flag it guards against."
        canonical="/assessments"
      />

      {/* HERO — statement + one anchor question */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-6">
        <p className="eyebrow flex items-center gap-1.5">
          <Sparkles size={12} />
          Assessments · anatomy of a question
        </p>
        <h1
          className="text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            lineHeight: 1.05,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            margin: 0,
            maxWidth: '20ch',
          }}
        >
          One question. Every angle Kollez sees.
        </h1>
        <p className="text-lg text-ink-secondary leading-8 m-0" style={{ maxWidth: '58ch' }}>
          Every other assessments page shows you feature grids. This one
          shows you a real SQL question and dissects it — every field
          the platform tracks, every mode it can be delivered in, every
          integrity flag it guards against. Grounded in the shipping
          data model. Nothing aspirational.
        </p>
      </section>

      {/* THE QUESTION — big card with annotations around it */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-8 lg:py-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] items-start">
          {/* The question card itself */}
          <div
            className="rounded-lg overflow-hidden shadow-md card"
            style={{ position: 'relative' }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgb(var(--bg-warning))', color: 'rgb(var(--ink-warning))' }}>MEDIUM</span>
                <span className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>SQL · Data &amp; Analytics · Q 18 of 36</span>
              </div>
              <span className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>90s</span>
            </div>
            <div className="p-6 grid gap-4">
              <p
                className="font-serif m-0"
                style={{
                  fontSize: '1.25rem',
                  lineHeight: 1.4,
                  color: 'rgb(var(--ink-primary))',
                  letterSpacing: '-0.005em',
                }}
              >
                {QUESTION_STEM}
              </p>
              <div className="grid gap-2">
                {QUESTION_OPTIONS.map((o) => (
                  <div
                    key={o.key}
                    className="rounded-md p-3 border flex items-center gap-3 text-sm"
                    style={{
                      borderColor: o.correct ? 'rgb(var(--ink-success))' : 'rgb(var(--line-secondary))',
                      background: o.correct ? 'rgb(var(--bg-success))' : 'rgb(var(--bg-primary))',
                    }}
                  >
                    <span
                      className="inline-flex items-center justify-center rounded font-semibold text-xs"
                      style={{
                        width: 22, height: 22,
                        background: o.correct ? 'rgb(var(--ink-success))' : 'rgb(var(--bg-tertiary))',
                        color: o.correct ? 'rgb(var(--ink-inverse))' : 'rgb(var(--ink-primary))',
                      }}
                    >
                      {o.key.toUpperCase()}
                    </span>
                    <span>{o.text}</span>
                    {o.correct && (
                      <span className="ml-auto text-[10px] font-medium" style={{ color: 'rgb(var(--ink-success))' }}>correct</span>
                    )}
                  </div>
                ))}
              </div>
              <div
                className="rounded-md p-3 text-xs"
                style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
              >
                <div className="font-medium mb-1" style={{ color: 'rgb(var(--ink-accent))' }}>Explanation (shown after answer)</div>
                Window functions rank across rows in a partition without collapsing them. GROUP BY collapses rows to aggregates. Subqueries and CTEs can express the same query but usually with an aggregation that hides rows.
              </div>
            </div>
          </div>

          {/* Anatomy fields — dl to the right */}
          <div className="grid gap-4">
            <div>
              <p className="eyebrow m-0 text-xs">Every field the platform tracks</p>
              <h2
                className="text-ink-primary mt-1"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: '1.5rem',
                  lineHeight: 1.15,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  margin: 0,
                }}
              >
                Nine metadata fields on this one question.
              </h2>
            </div>
            <dl className="grid gap-3">
              {ANATOMY_FIELDS.map((f) => (
                <div key={f.label} className="grid gap-1 pb-3" style={{ borderBottom: '1px solid rgb(var(--line-tertiary))' }}>
                  <div className="flex items-baseline justify-between gap-2">
                    <dt
                      className="text-[10px] uppercase tracking-widest font-medium"
                      style={{ color: 'rgb(var(--ink-tertiary))' }}
                    >
                      {f.label}
                    </dt>
                    <dd className="text-sm font-medium m-0" style={{ color: 'rgb(var(--ink-primary))' }}>{f.value}</dd>
                  </div>
                  <p className="text-xs m-0" style={{ color: 'rgb(var(--ink-tertiary))' }}>{f.hint}</p>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* QUALITY SIGNALS — a data strip */}
      <section
        className="border-y border-line-tertiary py-14 lg:py-16"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow m-0 text-xs">Quality signals — computed nightly</p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              How this question performs across 1,242 attempts.
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {QUALITY_SIGNALS.map((s) => (
              <div key={s.label} className="grid gap-1">
                <div
                  className="text-[10px] uppercase tracking-widest font-medium"
                  style={{ color: 'rgb(var(--ink-tertiary))' }}
                >
                  {s.label}
                </div>
                <div
                  className="font-serif tabular-nums"
                  style={{
                    fontSize: '2rem',
                    fontWeight: 500,
                    lineHeight: 1,
                    color: s.tone === 'good' ? 'rgb(var(--ink-success))' : s.tone === 'watch' ? 'rgb(var(--ink-warning))' : 'rgb(var(--ink-primary))',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {s.value}
                </div>
                <p className="text-xs m-0 mt-1" style={{ color: 'rgb(var(--ink-tertiary))' }}>{s.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAME QUESTION, SIX DELIVERIES */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-8">
        <div className="max-w-2xl">
          <p className="eyebrow m-0 text-xs">Same question · six deliveries</p>
          <h2
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              margin: 0,
            }}
          >
            One question. Six ways to ask it.
          </h2>
          <p className="mt-3 text-ink-secondary m-0" style={{ maxWidth: '58ch' }}>
            Screening wants a fixed set. Practice wants adaptive. Interview
            prep wants a conversation. Coding roles want an editor.
            Business roles want a case. HR wants voice. Pick the shape
            that fits the job.
          </p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {MODES.map((m) => (
            <article key={m.key} className="grid gap-3">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="flex items-center justify-center rounded-md"
                  style={{ width: 24, height: 24, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
                >
                  {m.icon}
                </span>
                <h3 className="font-serif text-lg m-0" style={{ letterSpacing: '-0.005em' }}>{m.name}</h3>
                <span className="text-xs ml-auto" style={{ color: 'rgb(var(--ink-tertiary))' }}>{m.who}</span>
              </div>
              <p className="helper m-0">{m.hint}</p>
              {m.render}
            </article>
          ))}
        </div>
      </section>

      {/* BANK BUILDER — how questions get into the bank */}
      <section
        className="border-y border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8 lg:grid-cols-[1fr_1fr] items-start">
          <div>
            <p className="eyebrow m-0 text-xs">The bank</p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Every question you saw above comes from somewhere.
            </h2>
            <p className="mt-3 text-ink-secondary m-0" style={{ maxWidth: '58ch' }}>
              Kollez ships a starter public bank. Every institution has a
              private bank. Partners can set drive-specific banks. Nexie
              can draft a fresh set from a job description — every
              AI-drafted question is queued for human review before it
              ships.
            </p>
          </div>

          {/* Bank sources — stack of source cards */}
          <div className="grid gap-3">
            {[
              { label: 'Kollez public bank',    detail: '~4,200 questions across 6 skill tracks. Curated + versioned.', tag: 'shipping', tone: 'success' },
              { label: 'Your institution bank', detail: 'Private to your tenant. HoD or TPO authors. Reviewer approves.', tag: 'per tenant', tone: 'info' },
              { label: 'Partner drive bank',    detail: 'Scoped to one hiring campaign. Partner reviews.', tag: 'scoped', tone: 'info' },
              { label: 'Nexie-drafted',         detail: 'AI drafts from a JD or topic. `source: ai` marker. Human review required.', tag: 'AI-draft', tone: 'warning' },
            ].map((s) => (
              <div key={s.label} className="rounded-lg p-4 card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs mt-1" style={{ color: 'rgb(var(--ink-secondary))' }}>{s.detail}</div>
                  </div>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium flex-shrink-0"
                    style={{
                      background: s.tone === 'success' ? 'rgb(var(--bg-success))' : s.tone === 'warning' ? 'rgb(var(--bg-warning))' : 'rgb(var(--bg-info))',
                      color: s.tone === 'success' ? 'rgb(var(--ink-success))' : s.tone === 'warning' ? 'rgb(var(--ink-warning))' : 'rgb(var(--ink-accent))',
                    }}
                  >
                    {s.tag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BADGE — what a graded pass emits */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-8 lg:grid-cols-[1fr_0.9fr] items-start">
        <div>
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <BadgeCheck size={12} />
            The outcome
          </p>
          <h2
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              margin: 0,
            }}
          >
            A pass emits a Badge owned by the student.
          </h2>
          <p className="mt-3 text-ink-secondary m-0" style={{ maxWidth: '58ch' }}>
            Every future opportunity whose workflow includes the same
            skill + level auto-satisfies the stage. The student skips
            the retake. Consent-controlled per DPDP — the student
            decides which opportunities see which badges.
          </p>
        </div>
        {/* Badge preview */}
        <div
          className="rounded-lg overflow-hidden card"
          style={{ borderColor: 'rgb(var(--ink-accent))', boxShadow: '0 0 0 1px rgb(var(--ink-accent))' }}
        >
          <div className="p-5 grid gap-3">
            <div className="flex items-center gap-3">
              <span
                className="flex items-center justify-center rounded-lg"
                style={{ width: 48, height: 48, background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
              >
                <BadgeCheck size={24} />
              </span>
              <div>
                <div className="font-serif text-lg leading-tight">SQL · Level 3</div>
                <div className="text-xs" style={{ color: 'rgb(var(--ink-tertiary))' }}>Skill · earned 12 Sept 2026</div>
              </div>
            </div>
            <dl className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Issuer tier</dt>
                <dd className="font-medium m-0">Tier 1 · Kollez proctored</dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Status</dt>
                <dd className="m-0" style={{ color: 'rgb(var(--ink-success))', fontWeight: 500 }}>active</dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Freshness</dt>
                <dd className="m-0">24 months</dd>
              </div>
              <div className="flex justify-between">
                <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Portable to</dt>
                <dd className="m-0">every partner drive</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* INTEGRITY — 7 flags, human review, appeal */}
      <section
        className="border-y border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
              <ScanSearch size={12} />
              Integrity
            </p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              What the platform watches. And what it never does.
            </h2>
            <p className="mt-3 text-ink-secondary m-0" style={{ maxWidth: '58ch' }}>
              Seven kinds of flag run in the background during
              proctored assessments. No flag ever auto-voids an attempt
              — every one is reviewed by staff on your team. 7-day
              appeal window on any voided attempt.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              'answerMatch',
              'faceMismatch',
              'impossibleSpeed',
              'tabSwitch',
              'paste',
              'codeSimilarity',
              'networkTiming',
            ].map((k) => (
              <div key={k} className="rounded-lg p-3 card flex items-center gap-2">
                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgb(var(--ink-warning))', display: 'inline-block' }} />
                <span className="text-sm font-mono" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{k}</span>
              </div>
            ))}
            <div
              className="rounded-lg p-3 flex items-center gap-2"
              style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-primary))' }}
            >
              <ShieldCheck size={14} style={{ color: 'rgb(var(--ink-success))' }} />
              <span className="text-sm font-medium">All human-reviewed</span>
            </div>
          </div>
          <p className="text-xs m-0" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            Accommodations are respected. Extra-time percentages are set per candidate and applied automatically — the proctoring does not treat accommodated time as suspicious.
          </p>
        </div>
      </section>

      {/* WHO USES WHAT — three persona rows */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-6">
        <div className="max-w-2xl">
          <p className="eyebrow m-0 text-xs">Who uses which mode</p>
          <h2
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              margin: 0,
            }}
          >
            Same bank. Three jobs.
          </h2>
        </div>
        <dl className="grid gap-6 mt-4">
          {[
            { p: 'Students', body: 'Adaptive practice + Nexie-explained wrongs + mock interviews. Every pass earns a badge you keep for life. Free forever.', to: '/for-students' },
            { p: 'Partners', body: 'Set a fixed test, or let Nexie draft a fresh 20-question set from your JD. Filter shortlist by verified badges. Live coding with real execution.', to: '/for-partners' },
            { p: 'Institutions', body: 'Proctored drive assessments with sign-off checks. Yearly skills checks. First-year onboarding. Signed score reports for NIRF / NAAC.', to: '/for-institutions' },
          ].map((r) => (
            <div key={r.p} className="grid gap-1 pb-4" style={{ borderBottom: '1px solid rgb(var(--line-tertiary))' }}>
              <div className="flex items-baseline gap-3 flex-wrap">
                <dt className="font-serif text-xl m-0" style={{ letterSpacing: '-0.005em' }}>{r.p}</dt>
                <Link to={r.to} className="link text-sm inline-flex items-center gap-1 ml-auto">
                  Explore {r.p.toLowerCase()} <ArrowRight size={12} />
                </Link>
              </div>
              <dd className="text-ink-secondary m-0" style={{ maxWidth: '68ch' }}>{r.body}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* FAQ — kept, but slim */}
      <section className="border-t border-line-tertiary py-16 lg:py-20" style={{ background: 'rgb(var(--bg-primary))' }}>
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <FaqAccordion
            heading="Common questions"
            emitJsonLd
            items={FAQ}
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Try a real Kollez question.
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Students take practice tests free. Institutions and partners
          book a demo to see the bank, the drive-assessment surface,
          and the integrity review flow.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Try Kollez assessments — free', to: '/register/student' }}
            secondary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            align="center"
          />
        </div>
      </section>
    </>
  );
}
